import * as React from "react";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import { Checkbox } from "./ui/checkbox";
import { CaretDown, X, MagnifyingGlass } from "@phosphor-icons/react";
import { cn } from "../lib/utils";

export function MultiSelect({
  options,
  value = [],
  onChange,
  placeholder = "Any",
  className,
  searchable = false,
  testId,
}) {
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState("");
  const filtered = React.useMemo(
    () => (q ? options.filter((o) => String(o).toLowerCase().includes(q.toLowerCase())) : options),
    [q, options]
  );

  const toggle = (opt) => {
    if (value.includes(opt)) onChange(value.filter((v) => v !== opt));
    else onChange([...value, opt]);
  };

  const display =
    value.length === 0
      ? placeholder
      : value.length === 1
      ? value[0]
      : `${value.length} selected`;

  const clear = (e) => {
    e.stopPropagation();
    onChange([]);
  };

  // When 1-3 are selected, show them inline as small chips on the trigger
  const showChips = value.length > 0 && value.length <= 3;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className={cn(
            "w-full min-h-10 px-2 py-1 text-sm border border-slate-300 rounded-md bg-white flex items-center justify-between hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300 transition-colors",
            className
          )}
        >
          {showChips ? (
            <div className="flex flex-wrap gap-1 items-center flex-1 min-w-0">
              {value.map((v) => (
                <span key={v} className="inline-flex items-center gap-1 bg-slate-900 text-white text-[10px] uppercase tracking-[0.08em] font-semibold px-1.5 py-0.5 rounded">
                  {v}
                </span>
              ))}
            </div>
          ) : (
            <span className={cn("truncate text-left flex-1", value.length === 0 && "text-slate-500")}>{display}</span>
          )}
          {value.length > 0 ? (
            <span onClick={clear} className="ml-1 inline-flex items-center justify-center h-5 w-5 rounded hover:bg-slate-200 text-slate-500 hover:text-slate-800 shrink-0" aria-label="Clear">
              <X size={12} />
            </span>
          ) : (
            <CaretDown size={12} className="ml-2 text-slate-400 shrink-0" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        {searchable && (
          <div className="border-b border-slate-200 p-2">
            <div className="relative">
              <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search…"
                className="w-full h-7 pl-7 pr-2 text-xs border border-slate-200 rounded outline-none focus:border-slate-400"
              />
            </div>
          </div>
        )}
        <div className="max-h-72 overflow-y-auto p-1">
          {filtered.length === 0 && <div className="px-3 py-2 text-xs text-slate-500">No matches</div>}
          {filtered.map((opt) => {
            const checked = value.includes(opt);
            return (
              <label
                key={opt}
                className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-100 cursor-pointer text-sm"
              >
                <Checkbox checked={checked} onCheckedChange={() => toggle(opt)} />
                <span className="truncate">{opt}</span>
              </label>
            );
          })}
        </div>
        {value.length > 0 && (
          <div className="border-t border-slate-200 p-2 flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-[0.12em] text-slate-500 font-semibold">{value.length} selected</span>
            <button onClick={() => onChange([])} className="text-[11px] text-slate-600 hover:text-slate-900 underline">Clear</button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default MultiSelect;
